/**
 * Prefab library - named groups of object instances. Games can save a
 * selection of instances as a named prefab, then drop the whole group back
 * into the scene on any floor. The store itself is in-memory; the host
 * hydrates it per project and persists changes (the studio keeps prefabs in
 * the project's docs store so they follow the project across machines).
 */
import { create } from 'zustand';
import type { MapDecal, MapLight, MapObjectInstance, MapTriggerVolume } from '../../content/schema/map';

const LEGACY_STORAGE_KEY = 'sstk:editor:prefabs';

/** The pieces of a selection a prefab can capture, keyed by collection. */
export interface PrefabSelection {
  instances: MapObjectInstance[];
  lights?: MapLight[];
  triggers?: MapTriggerVolume[];
  decals?: MapDecal[];
}

export interface Prefab {
  id: string;
  name: string;
  /** Instances stored with positions relative to the group centroid. */
  instances: MapObjectInstance[];
  /** Lights captured in the same selection, positions relative to the centroid. Optional: absent on prefabs saved before lights were supported. */
  lights?: MapLight[];
  /** Triggers captured in the same selection, centers relative to the centroid. Optional: absent on prefabs saved before triggers were supported. */
  triggers?: MapTriggerVolume[];
  /** Decals captured in the same selection, positions relative to the centroid. Optional: absent on prefabs saved before decals were supported. */
  decals?: MapDecal[];
  /** ISO timestamp of when this prefab was created. */
  createdAt: string;
}

interface PrefabState {
  prefabs: Prefab[];
  /** Replace the library wholesale (project hydration). */
  hydratePrefabs: (prefabs: Prefab[]) => void;
  /** Save the given selection under a name. Returns the new prefab id. */
  savePrefab: (name: string, selection: PrefabSelection) => string;
  /** Permanently delete a prefab by id. */
  deletePrefab: (id: string) => void;
  /** Reorder (rename) a prefab. */
  renamePrefab: (id: string, name: string) => void;
}

/**
 * Prefabs saved by the pre-project localStorage store, for a one-time lift
 * into the project document; cleared after a successful read.
 */
export function takeLegacyLocalPrefabs(): Prefab[] {
  try {
    const raw = localStorage.getItem(LEGACY_STORAGE_KEY);
    if (!raw) return [];
    const prefabs = JSON.parse(raw) as Prefab[];
    localStorage.removeItem(LEGACY_STORAGE_KEY);
    return prefabs;
  } catch {
    return [];
  }
}

/**
 * Pulls every piece of a selection - instances, lights, triggers, decals -
 * out of the map editor's live entity arrays, filtered to the given
 * selected ids. Every "save this selection as a prefab" UI (the legacy
 * MapEditorOverlay's PrefabLibrary, Studio's DockPrefabs) should build its
 * `PrefabSelection` through this, not by hand-filtering each array itself -
 * that's exactly how PrefabLibrary.tsx's own version once shipped filtering
 * only `objectInstances`, silently dropping any selected light/trigger/decal
 * with no error. Returns real arrays (never omitted), so a caller doesn't
 * need `?? []` to read counts back out.
 */
export function selectionToPrefab(
  entities: {
    objectInstances: MapObjectInstance[];
    lights: MapLight[];
    triggers: MapTriggerVolume[];
    decals: MapDecal[];
  },
  selectedIds: string[],
): Required<PrefabSelection> {
  return {
    instances: entities.objectInstances.filter((i) => selectedIds.includes(i.id)),
    lights: entities.lights.filter((l) => selectedIds.includes(l.id)),
    triggers: entities.triggers.filter((t) => selectedIds.includes(t.id)),
    decals: entities.decals.filter((d) => selectedIds.includes(d.id)),
  };
}

/**
 * Compute the XZ centroid of a set of world-space XZ points so positions can
 * be stored relative to the group centre.
 */
function centroidOf(points: Array<[number, number]>): [number, number] {
  if (points.length === 0) return [0, 0];
  const sumX = points.reduce((s, [x]) => s + x, 0);
  const sumZ = points.reduce((s, [, z]) => s + z, 0);
  return [sumX / points.length, sumZ / points.length];
}

export const usePrefabStore = create<PrefabState>((set) => ({
  prefabs: [],

  hydratePrefabs: (prefabs) => set({ prefabs }),

  savePrefab: (name, selection) => {
    const instances = selection.instances;
    const lights = selection.lights ?? [];
    const triggers = selection.triggers ?? [];
    const decals = selection.decals ?? [];

    // Store positions relative to the whole selection's centroid (across
    // every kind captured) so the prefab can be dropped anywhere and every
    // member keeps its position relative to the others.
    const [cx, cz] = centroidOf([
      ...instances.map((i): [number, number] => [i.transform.position[0], i.transform.position[2]]),
      ...lights.map((l): [number, number] => [l.position[0], l.position[2]]),
      ...decals.map((d): [number, number] => [d.position[0], d.position[2]]),
      ...triggers.map((t): [number, number] => [t.center[0], t.center[1]]),
    ]);

    const relativeInstances: MapObjectInstance[] = instances.map((inst) => ({
      ...inst,
      transform: {
        ...inst.transform,
        position: [
          inst.transform.position[0] - cx,
          inst.transform.position[1],
          inst.transform.position[2] - cz,
        ],
      },
    }));
    const relativeLights: MapLight[] = lights.map((light) => ({
      ...light,
      position: [light.position[0] - cx, light.position[1], light.position[2] - cz],
    }));
    const relativeTriggers: MapTriggerVolume[] = triggers.map((trigger) => ({
      ...trigger,
      center: [trigger.center[0] - cx, trigger.center[1] - cz],
    }));
    const relativeDecals: MapDecal[] = decals.map((decal) => ({
      ...decal,
      position: [decal.position[0] - cx, decal.position[1], decal.position[2] - cz],
    }));

    const id = `prefab-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const prefab: Prefab = {
      id,
      name: name.trim() || 'Untitled Prefab',
      instances: relativeInstances,
      lights: relativeLights,
      triggers: relativeTriggers,
      decals: relativeDecals,
      createdAt: new Date().toISOString(),
    };
    set((state) => ({ prefabs: [prefab, ...state.prefabs] }));
    return id;
  },

  deletePrefab: (id) =>
    set((state) => ({ prefabs: state.prefabs.filter((p) => p.id !== id) })),

  renamePrefab: (id, name) =>
    set((state) => ({
      prefabs: state.prefabs.map((p) =>
        p.id === id ? { ...p, name: name.trim() || p.name } : p,
      ),
    })),
}));
